import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Collapse from '@kunukn/react-collapse'
import cls from 'classnames'

import { collapsibleClick } from '@cred/react-analytics/events'
import { withSnowplowContexts } from '@cred/react-analytics/SnowplowContextManager'

import { PartnerLogo } from 'components/partner-logo'
import { fireGtmEvent } from 'utils/gtm'
import RateTableCtaButton from 'components/RateTableCtaButton'
import { addTrackingParamsToUrl, getQueryParamFromUrl } from 'utils/urls'
import { useViewport } from 'components/hooks'
import mapLenderSlagToTrackingName from 'utils/mapLenderSlagToTrackingName'
import { INSCHOOL } from 'common/const/verticalNames'
import RateTableDisclosureButton from 'components/RateTableDisclosureButton'
import RateTableAprValue from 'components/RateTableAprValue'
import { getRedirectHrefWithParams } from 'components/featured-lender-banner/LenderCard/utils'

import disclaimers from './disclaimers'
import styles from './styles.module.scss'

const DIRECT_LENDER_LINKS = ['sallie-mae', 'cas']

function getRoleValue(ctaUrl) {
  if (!ctaUrl) {
    return ''
  }

  try {
    const url = new URL(ctaUrl)
    return getQueryParamFromUrl('role', url.search)
  } catch (err) {
    console.error('[getRoleValue function error]: ', { ctaUrl }, err)
    return ''
  }
}

const LenderRate = ({
  trackSnowplowEvent,
  lender,
  widgetContext,
  shouldDarkenBorder,
  theme,
  index,
  // eslint-disable-next-line react/prop-types
  ctaUrl,
}) => {
  const {
    options: { uniqueId },
    parentPage: { partnerURL },
    vertical,
  } = widgetContext

  const [open, setOpen] = useState(false)
  const { xSmall } = useViewport()

  const toggleLendersDisclosuresModal = () => {
    trackSnowplowEvent(collapsibleClick, {
      text: 'Disclaimers',
      action: open ? 'collapse' : 'expand',
    })

    setOpen((prevOpen) => !prevOpen)
  }

  const onClickCta = () => {
    const lenderData = { lender: lender.name }

    fireGtmEvent('ctaClick', Object.assign(lenderData, widgetContext))
  }

  const role = getRoleValue(ctaUrl)

  const getRedirectUrl = getRedirectHrefWithParams({
    lenderSlug: lender.slug,
    position: index + 1,
    pageUrl: partnerURL,
    uniqueId,
    vertical,
    theme,
  })
  const checkRatesUrl = getRedirectUrl('credible-marketplace', { role })
  const redirectUrl = getRedirectUrl('redirect')

  const darkenBorderClass = shouldDarkenBorder ? styles.darkenedBorder : ''
  const themeStylesClassName = styles[theme] ? styles[theme] : ''

  const ctaUrlWithTrackingParams = addTrackingParamsToUrl({
    widgetContext,
    lenderSlug: mapLenderSlagToTrackingName(lender.slug, INSCHOOL),
    url: checkRatesUrl,
  })

  const isDirectLenderLink = DIRECT_LENDER_LINKS.includes(lender.slug)
  const disclaimerText = isDirectLenderLink ? 'On lender site' : "on Credible's website"

  return (
    <div
      data-test-id={`rateTableTile_${lender.rateSlug}`}
      className={`${styles.panel} ${darkenBorderClass}`}
    >
      <div className="pure-g">
        <div className={cls('pure-u-1 pure-u-md-1-4 pure-u-sm-1-1', styles.logoWrap)}>
          <div className={styles.logo}>
            <PartnerLogo partner={lender.slug} />
          </div>
          {disclaimers[lender.slug] && !xSmall && (
            <RateTableDisclosureButton
              open={open}
              onClick={toggleLendersDisclosuresModal}
              isMobile={xSmall}
              className={styles.disclaimerWrapperDesktop}
            />
          )}
        </div>
        <RateTableAprValue
          minRate={lender.minFixedRate}
          maxRate={lender.maxFixedRate}
          withBottomBorder
        />
        {lender.minVariableRate && lender.maxVariableRate ? (
          <RateTableAprValue
            minRate={lender.minVariableRate}
            maxRate={lender.maxVariableRate}
            isVariable
          />
        ) : (
          <div className="pure-u-1 pure-u-md-1-4 pure-u-sm-1-3" />
        )}
        <div className={cls('pure-u-1 pure-u-md-1-4 pure-u-sm-1-3', styles.btnWrap)}>
          <RateTableCtaButton
            data-testid="cta-button"
            containerClassName={styles.CTAdesktop}
            target="_blank"
            transparent={!xSmall}
            href={isDirectLenderLink ? redirectUrl : ctaUrlWithTrackingParams}
            onClick={onClickCta}
            fullWidth
            btnText="Check Rates"
            disclaimerText={lender.slug === 'sallie-mae' ? 'from Sallie Mae' : disclaimerText}
            disclaimerClassName="align-center"
            vertical="student-loan"
          />
          {disclaimers[lender.slug] && xSmall && (
            <RateTableDisclosureButton
              open={open}
              onClick={toggleLendersDisclosuresModal}
              isMobile={xSmall}
              className={styles.disclaimerWrapperMobile}
            />
          )}
        </div>
        <div className="pure-u-1">
          <Collapse className={styles.dropdown} isOpen={open}>
            <div
              className={`${styles.disclosure} ${themeStylesClassName} mt-24 mb-16 disclaimer neutral500`}
            >
              {disclaimers[lender.slug]}
            </div>
          </Collapse>
        </div>
      </div>
    </div>
  )
}

LenderRate.propTypes = {
  index: PropTypes.number.isRequired,
  lender: PropTypes.object,
  widgetContext: PropTypes.object,
  trackSnowplowEvent: PropTypes.func,
  shouldDarkenBorder: PropTypes.bool,
  theme: PropTypes.string,
}

export { LenderRate }

export default withSnowplowContexts(LenderRate)
